import { useMemo } from 'react';
import { Alert, DropDown, FormField } from '@elementary/common-ui/components';
import { FivetranDestinationGroup } from 'shared/types';

type Props = {
  groups: FivetranDestinationGroup[];
  selectedIds: string[];
  lockedIds: string[];
  onChange: (ids: string[]) => void;
  errorMessage?: string;
  disabled?: boolean;
};

const FivetranDestinationGroupsStep = ({
  groups,
  selectedIds,
  lockedIds,
  onChange,
  errorMessage,
  disabled,
}: Props) => {
  const options = useMemo(
    () =>
      groups.map((group) => ({
        value: group.id,
        label: group.name,
        description: group.region ?? undefined,
        disabled: lockedIds.includes(group.id),
      })),
    [groups, lockedIds],
  );

  // Add 'all' sentinel when every option is selected so the Select All
  // checkbox reflects the state when options were picked individually.
  const allOptionIds = useMemo(() => groups.map((g) => g.id), [groups]);
  const allSelected =
    allOptionIds.length > 0 &&
    allOptionIds.every((id) => selectedIds.includes(id));
  const displayValue = allSelected ? [...selectedIds, 'all'] : selectedIds;

  const handleChange = (ids: string[]) => {
    const filtered = ids.filter((id) => id !== 'all');
    // Locked items must stay selected even when "Unselect All" is triggered
    const withLocked = [...new Set([...filtered, ...lockedIds])];
    onChange(withLocked);
  };

  return (
    <FormField title="Select destination groups to monitor">
      {groups.length === 0 ? (
        <Alert severity="info">
          No destination groups were found in this Fivetran account.
        </Alert>
      ) : (
        <DropDown
          multiple
          showSearch
          showClear={false}
          sortOptions={false}
          size="large"
          placeholder="Select destination groups"
          options={options}
          value={displayValue}
          onChange={handleChange}
          disabled={disabled}
          MenuProps={{
            disableScrollLock: true,
            sx: { maxWidth: '520px', maxHeight: 370, mt: 1 },
          }}
        />
      )}

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
    </FormField>
  );
};

export default FivetranDestinationGroupsStep;
